// SPDX-License-Identifier: GPL-3.0-or-later
// One preallocated cloud of additive point sprites. The arrays and the geometry never grow.
// The owner writes the arrays and calls `commit`. A point has a position, a colour and a size
// in table units. The size on screen is the size over the distance to the camera.
// The shader is built in code. It needs no file and no inline script (the CSP allows it).

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  DynamicDrawUsage,
  Points,
  ShaderMaterial,
} from "three";

const VERTEX = `
attribute vec3 aColor;
attribute float aSize;
uniform float uScale;
varying vec3 vColor;
void main() {
  vColor = aColor;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = aSize * uScale / max(-mv.z, 1.0);
  gl_Position = projectionMatrix * mv;
}
`;

const FRAGMENT = `
varying vec3 vColor;
void main() {
  float d = length(gl_PointCoord - vec2(0.5));
  float a = smoothstep(0.5, 0.0, d);
  gl_FragColor = vec4(vColor, a);
}
`;

/** A cloud of point sprites. */
export interface PointCloud {
  readonly cap: number;
  readonly points: Points;
  /** x, y, z of each point (three.js frame). */
  readonly position: Float32Array;
  /** r, g, b of each point. A fade scales the colour toward black. */
  readonly color: Float32Array;
  /** Size of each point in table units. 0 hides a point. */
  readonly size: Float32Array;
  /** Show the first `live` points and send the arrays to the GPU. */
  commit(live: number): void;
  /** The pixel scale: viewport height in pixels over twice the tangent of the half field of view. */
  setScale(pixelsPerUnit: number): void;
  dispose(): void;
}

/** Make a cloud with room for `cap` points. */
export function createPointCloud(cap: number): PointCloud {
  const position = new Float32Array(cap * 3);
  const color = new Float32Array(cap * 3);
  const size = new Float32Array(cap);
  const positionAttr = new BufferAttribute(position, 3).setUsage(DynamicDrawUsage);
  const colorAttr = new BufferAttribute(color, 3).setUsage(DynamicDrawUsage);
  const sizeAttr = new BufferAttribute(size, 1).setUsage(DynamicDrawUsage);
  const scale = { value: 600 };
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", positionAttr);
  geometry.setAttribute("aColor", colorAttr);
  geometry.setAttribute("aSize", sizeAttr);
  geometry.setDrawRange(0, 0);
  const material = new ShaderMaterial({
    uniforms: { uScale: scale },
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  const points = new Points(geometry, material);
  points.frustumCulled = false;
  points.renderOrder = 10;
  return {
    cap,
    points,
    position,
    color,
    size,
    commit(live) {
      geometry.setDrawRange(0, Math.max(0, Math.min(cap, live)));
      positionAttr.needsUpdate = true;
      colorAttr.needsUpdate = true;
      sizeAttr.needsUpdate = true;
    },
    setScale(pixelsPerUnit) {
      if (Number.isFinite(pixelsPerUnit) && pixelsPerUnit > 0) scale.value = pixelsPerUnit;
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}
